import React, { forwardRef, useImperativeHandle, useState } from "react";
import "@progress/kendo-theme-default/dist/all.css";
import {
    Sidebar,
    Menu,
    MenuItem,
    SubMenu,
    useProSidebar,
    sidebarClasses,
    menuClasses,
} from "react-pro-sidebar";
import { sideBarData as allMenusData } from "./MenuData.ts";
import { ProfileBox } from "../layout/ProfileBox.jsx";
import styles from "./Sidebar.module.scss";
import { AnimatedLogo } from "./AnimatedLogo.jsx";
import { CollapsedFlyout } from "./CollapsedFlyout.jsx";
import flyoutStyles from "./CollapsedFlyout.module.scss";
import { FLYOUT_TRIGGER_ATTR, useSidebarFlyouts } from "./useSidebarFlyouts";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAllowedLiscence } from "hooks/useAllowedLiscence";
import MenuItemId from "./MenuItemId.ts";
import { useAccessRightChecker } from "../UserAccessRights.ts";

const SideBarIcons = (props) => {
    let menuItemFlag = props.data.flag;
    return (
        <>
            <div className={"rounded-circle align-items-center " + styles["dot"]} />
            <div
                className={
                    styles["bar"] + " " + (menuItemFlag ? styles[`bar${menuItemFlag}`] : "")
                }
            />
        </>
    );
};

const SideBar = forwardRef((props, ref) => {
    const { collapseSidebar, collapsed } = useProSidebar();
    const [collapse, setCollapse] = useState(false);
    const [sideBarData, setSidebarData] = useState([]);
    const { allowedList } = useAllowedLiscence();
    const hasAccess = useAccessRightChecker();


    useImperativeHandle(ref, () => ({
        toggleSidebar() {
            collapseSidebar();
        },
    }));
    const location = useLocation();

    const active = () => {
        const pathname = (location.pathname || "").toLowerCase();
        if (!pathname || pathname === "/") return "/dashboard";

        const firstSegment = pathname.split("/")[1];
        return firstSegment ? `/${firstSegment}` : "/dashboard";
    };

    const isRouteActive = (route) => {
        const normalizedPath = (location.pathname || "").toLowerCase();
        const normalizedRoute = (route || "").toLowerCase();
        if (!normalizedRoute) return false;
        return (
            normalizedPath === normalizedRoute ||
            normalizedPath.startsWith(`${normalizedRoute}/`)
        );
    };

    // Collapsed sidebar: hover labels for plain items, child-link panels for the rest.
    const flyouts = useSidebarFlyouts({
        enabled: collapsed,
        sidebarSelector: `.${sidebarClasses.root}`,
    });
    const { hideTooltip } = flyouts;

    useEffect(hideTooltip, [hideTooltip, location.pathname]);

    let mainMenuItems = sideBarData.filter((item) => {
        return (
            item.parentId === undefined || item.parentId === "" || item.parentId === NaN
        );
    });

    const navigate = useNavigate();


    const applyFirstLastFlags = (items) => {
        const result = items.map((item) => {
            if (!item.parentId) return item;

            const itemCopy = { ...item };
            delete itemCopy.flag;
            return itemCopy;
        });

        const parentIds = [
            ...new Set(
                result
                    .filter((item) => item.parentId)
                    .map((item) => item.parentId)
            ),
        ];

        parentIds.forEach((parentId) => {
            const childIndexes = result
                .map((item, index) => item.parentId === parentId ? index : -1)
                .filter((index) => index !== -1);

            if (childIndexes.length === 0) return;

            result[childIndexes[0]] = {
                ...result[childIndexes[0]],
                flag: "first",
            };

            result[childIndexes[childIndexes.length - 1]] = {
                ...result[childIndexes[childIndexes.length - 1]],
                flag: "last",
            };
        });

        return result;
    };

    useEffect(() => {

        if (allowedList.length) {

            let filteredData = allMenusData?.filter((item) => {
                const hasRequiredAccess = !item.requiredAccess || hasAccess(item.requiredAccess);
                const isAllowedParent = item.parentId || allowedList.includes(item.tabId);
                return hasRequiredAccess && isAllowedParent;
            });

            filteredData = filteredData.filter((item) => {
                if (!item.parent) return true;

                return filteredData.some(
                    (child) => child.parentId === item.id
                );
            });

            filteredData = applyFirstLastFlags(filteredData);

            setSidebarData(filteredData);
        }
    }, [allowedList]);

    return (
        <>
            <Sidebar
                className="vh-100 border-0"
                backgroundColor="white"
                width="100%"
                transitionDuration="300"
                collapsedWidth="7rem"
                rootStyles={{
                    [`.${sidebarClasses.container}`]: {
                        borderRadius: "0px 25px 0px 0px",
                    },
                    [`.${menuClasses.root}`]: {
                        paddingLeft: "5%",
                        paddingRight: "5%",
                        
                    },
                    [`.${menuClasses.button}`]: {
                        marginBottom: "6px",
                        height: "1.875rem",
                        "&:hover": {
                            height: "1.875rem",
                        },
                        "&:hover": {
                            [`.${menuClasses.label}`]: {
                                opacity: "1",
                            },
                        },
                    },
                    [`.${menuClasses.label}`]: {
                        fontFamily: "Montserrat",
                        fontStyle: "normal",
                        fontWeight: 600,
                        fontSize: "1rem",
                        color: "#707070",
                        // opacity: '0.5',
                        lineHeight: "0.9rem",
                    },
                    [`.${menuClasses.SubMenuExpandIcon}`]: {
                        color: "#262626",
                        opacity: "0.5",
                    },

                    [`.${menuClasses.active}`]: {
                        [`.${menuClasses.label}`]: {
                            color: "#262626",
                            opacity: 1,
                        },
                        [`.${menuClasses.button}`]: {
                            backgroundColor: "#F4F4F3",
                            borderRadius: "5px",
                            height: "1.875rem",
                        },
                    },
                    [`.${menuClasses.subMenuRoot}`]: {
                        [`.${menuClasses.label}`]: {
                            lineHeight: "1.25rem",
                        },
                        [`.${menuClasses.subMenuContent}`]: {

                            overflow: "hidden !important",

                            [`.${menuClasses.button}`]: {
                                padding: "10px 0px 10px 1rem",
                                marginBottom: "0px",
                            },
                        },
                    },
                }}>

            <div className="d-flex flex-column h-100">
                <div className="text-center pt-4">
                    <AnimatedLogo collapsed={collapsed} />
                </div>

                <Menu
                    className="mt-3 flex-grow-1 overflow-auto"
                    iconShape="circle"
                    closeOnClick={true}
                    onScroll={flyouts.closeAll}
                    >
                    {mainMenuItems.map((item) => {
                        let subMenuItems = sideBarData.filter((i) => {
                            return i.parentId === item.id;
                        });

                        if (collapsed) {
                            const hasChildren = subMenuItems.length > 0;
                            const icon = <img src={item.icon} className="icon-styles" alt={item.id} />;
                            if (!hasChildren) {
                                const showLabel = (e) => flyouts.showTooltip(e, item.text);
                                return (
                                    <MenuItem
                                        key={item.id}
                                        active={active() === item.route}
                                        icon={icon}
                                        aria-label={item.text}
                                        onMouseEnter={showLabel}
                                        onMouseLeave={flyouts.hideTooltip}
                                        onClick={() => {
                                            flyouts.hideTooltip();
                                            navigate(item.route);
                                        }}
                                    />
                                );
                            }
                            const showPanel = (e) => flyouts.showPanel(e, item, subMenuItems);
                            return (
                                <MenuItem
                                    key={item.id}
                                    className={styles["has-children"]}
                                    active={subMenuItems.some((sub) =>
                                        isRouteActive(`${item.route}${sub.route}`)
                                    )}
                                    icon={icon}
                                    aria-label={item.text}
                                    aria-haspopup="menu"
                                    {...{ [FLYOUT_TRIGGER_ATTR]: true }}
                                    onMouseEnter={showPanel}
                                    onMouseLeave={flyouts.leavePanelArea}
                                    onClick={(e) => flyouts.togglePanel(e, item, subMenuItems)}
                                />
                            );
                        }

                        if (subMenuItems.length === undefined || subMenuItems.length === 0) {
                            return (
                                <MenuItem
                                    key={item.id}
                                    active={active() === item.route ? true : false}
                                    icon={<img src={item.icon} className="icon-styles" alt={item.id} />}
                                    onClick={() => {
                                        navigate(item.route);
                                    }}>
                                    {!collapsed && item.text}
                                </MenuItem>
                            );
                        } else {
                            return (
                                <SubMenu
                                    key={item.id}
                                    label={!collapsed && item.text}
                                    icon={<img src={item.icon} className="icon-styles" alt={item.id} />}
                                    open={collapse === item.text}
                                    onOpenChange={(e) => {
                                        if (e === true) {
                                            setCollapse(item.text);
                                        } else {
                                            setCollapse("");
                                        }
                                    }}>
                                    {subMenuItems.map((subItem) => {
                                        const fullRoute = `${item.route}${subItem.route}`;
                                        return (
                                            <MenuItem
                                                key={subItem.id}
                                                active={isRouteActive(fullRoute)}
                                                className={styles["submenu-item"]}
                                                icon={<SideBarIcons data={subItem} />}
                                                onClick={() => {
                                                    navigate(fullRoute);
                                                }}>
                                                <div>{subItem.text}</div>
                                            </MenuItem>
                                        );
                                    })}
                                </SubMenu>
                            );
                        }
                    })}
                </Menu>

                    <div>
                        {!collapsed ? (
                            <div className={`justify-content-center text-center`}>
                                <hr className="w-100 m-auto p-2" />
                                <ProfileBox />
                            </div>
                        ) : (
                            <div className="ms-3">
                                <ProfileBox collapsed />
                            </div>
                        )}
                    </div>

                </div>
            </Sidebar>
            <CollapsedFlyout
                variant="tooltip"
                role="tooltip"
                target={flyouts.tooltip?.target}
                open={flyouts.tooltipOpen}>
                {flyouts.tooltip?.text}
            </CollapsedFlyout>
            <CollapsedFlyout
                variant="panel"
                role="menu"
                containerRef={flyouts.panelRef}
                target={flyouts.panel?.target}
                open={flyouts.panelOpen}
                onMouseEnter={flyouts.enterPanel}
                onMouseLeave={flyouts.leavePanelArea}>
                {flyouts.panel && (
                    <>
                        <div className={flyoutStyles.title}>{flyouts.panel.item.text}</div>
                        <ul className={flyoutStyles.list}>
                            {flyouts.panel.children.map((child) => {
                                const route = `${flyouts.panel.item.route}${child.route}`;
                                return (
                                    <li key={child.id}>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            tabIndex={flyouts.panelOpen ? 0 : -1}
                                            className={`${flyoutStyles.link} ${
                                                isRouteActive(route) ? flyoutStyles.active : ""
                                            }`}
                                            onClick={() => {
                                                flyouts.closeAll();
                                                navigate(route);
                                            }}>
                                            <span className={flyoutStyles.timeline}>
                                                <SideBarIcons data={child} />
                                            </span>
                                            {child.text}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
            </CollapsedFlyout>
        </>
    );
});

export default SideBar;
